/**
 * AssetPreviewPopover - Lightweight inspector for media assets.
 * Shows preview with file type badge, duration, and clear action hierarchy.
 * Follows Libretto's calm, editorial design system.
 */
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Box, Typography, IconButton, Slider, Button} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import {useAssetStore} from "../../../storage/AssetStore";
import {AuthContext} from "../../../context/AuthContext";

interface MultiSelectPreviewItem {
    id: string;
    name: string;
    type: "audio" | "video";
    thumbnailUrl?: string;
}

interface AssetPreviewPopoverProps {
    anchorEl: HTMLElement | null;
    item: any | null;
    onClose: () => void;
    onInsert: (item: any) => void | Promise<void>;
    onAddNewLayer: (item: any) => void | Promise<void>;
    onCreateSequence?: () => void | Promise<void>;
    multiSelectCount?: number;
    multiSelectItems?: MultiSelectPreviewItem[];
    hideInsertIntoScript?: boolean;
}

const AssetPreviewPopover: React.FC<AssetPreviewPopoverProps> = ({
    anchorEl,
    item,
    onClose,
    onInsert,
    onAddNewLayer,
    onCreateSequence,
    multiSelectCount = 0,
    multiSelectItems = [],
    hideInsertIntoScript = false,
}) => {
    const open = Boolean(anchorEl);
    const isMultiSelectPreview = Number(multiSelectCount) > 1;
    const { GetTrackUrl, GetAssetUrl } = useAssetStore();
    const authContext = React.useContext(AuthContext);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const COLLAPSED_WIDTH = 380;
    const GAP = 12;
    const [isExpanded, setIsExpanded] = useState(false);
    const canExpand = !isMultiSelectPreview && (item?.type === "video" || item?.type === "image");
    const expanded = Boolean(isExpanded && canExpand);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isMultiSelectPreview) {
                setPreviewUrl(null);
                setDuration(0);
                setCurrentTime(0);
                setIsPlaying(false);
                return;
            }
            if (!item) return;
            setIsExpanded(false);
            try {
                if (item.type === 'image') {
                    const asset = item.record || {};
                    const url = await GetAssetUrl({ assetId: asset.assetId });
                    if (!cancelled) {
                        setPreviewUrl(url as string);
                        setDuration(0);
                        setCurrentTime(0);
                        setIsPlaying(false);
                    }
                    return;
                }
                const track = item.record;
                const trackId = track.trackId;
                const assetId = track.assetId;
                const projectId = track.projectId;
                const title = track.title;
                const userId = authContext.getUserId?.();
                // @ts-ignore
                const url = await GetTrackUrl({ trackId, assetId, projectId, title, userId });
                if (!cancelled) {
                    setPreviewUrl(url as string);
                    setDuration(0);
                    setCurrentTime(0);
                    setIsPlaying(false);
                }
            } catch (err) {
                console.error('Failed to open preview', err);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, isMultiSelectPreview]);

    const computePosition = () => {
        if (!anchorEl) return;
        const rect = anchorEl.getBoundingClientRect();
        const h = containerRef.current?.offsetHeight ?? 260;
        let left = rect.left - GAP - COLLAPSED_WIDTH;
        let top = rect.top + rect.height / 2 - h / 2;
        const minLeft = 8;
        const minTop = 8;
        const maxTop = window.innerHeight - h - 8;
        if (left < minLeft) left = minLeft;
        if (top < minTop) top = minTop;
        if (top > maxTop) top = maxTop;
        setPosition({ top: Math.round(top), left: Math.round(left) });
    };

    useLayoutEffect(() => {
        if (!anchorEl) return;
        if (expanded) return;
        computePosition();
        const onScroll = () => computePosition();
        const onResize = () => computePosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        const id = window.setTimeout(computePosition, 0);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            window.clearTimeout(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorEl, previewUrl, expanded]);

    const handleClose = () => {
        setIsExpanded(false);
        setIsPlaying(false);
        if (videoRef.current) videoRef.current.pause();
        if (audioRef.current) audioRef.current.pause();
        onClose();
    };

    const togglePlay = async () => {
        const media = item?.type === 'audio' ? audioRef.current : videoRef.current;
        if (!media) return;
        if (isPlaying) {
            media.pause();
            setIsPlaying(false);
        } else {
            try {
                await media.play();
                setIsPlaying(true);
            } catch {}
        }
    };

    const handleSeek = (_: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        const media = item?.type === 'audio' ? audioRef.current : videoRef.current;
        if (!media || !duration) return;
        media.currentTime = (v / 100) * duration;
    };

    const formatTime = (sec: number) => {
        if (!isFinite(sec)) return "0:00";
        const s = Math.floor(sec % 60).toString().padStart(2, '0');
        const m = Math.floor(sec / 60).toString();
        return `${m}:${s}`;
    };
    
    const formatDuration = (sec: number) => {
        if (!isFinite(sec) || sec === 0) return "";
        const s = Math.floor(sec % 60).toString().padStart(2, '0');
        const m = Math.floor(sec / 60).toString();
        return `${m}:${s}`;
    };

    // Truncate filename intelligently
    const truncateFilename = (name: string, maxLen: number = 32) => {
        if (!name || name.length <= maxLen) return name;
        const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
        const base = name.slice(0, name.length - ext.length);
        const truncLen = maxLen - ext.length - 3;
        return base.slice(0, truncLen) + '...' + ext;
    };

    // Get type badge info
    const getTypeBadge = () => {
        switch (item?.type) {
            case 'video':
                return { icon: <VideocamOutlinedIcon sx={{ fontSize: 12 }} />, label: 'Video', color: '#7C3AED' };
            case 'audio':
                return { icon: <AudiotrackOutlinedIcon sx={{ fontSize: 12 }} />, label: 'Audio', color: '#059669' };
            case 'image':
                return { icon: <ImageOutlinedIcon sx={{ fontSize: 12 }} />, label: 'Image', color: '#2563EB' };
            default:
                return null;
        }
    };

    if (!open || !anchorEl) return null;

    const assetCanBeInsertedIntoScript = !isMultiSelectPreview && item && (item.type === 'video' || item.type === 'audio');
    const showInsertIntoScript = assetCanBeInsertedIntoScript && !hideInsertIntoScript;
    const addLayerIsPrimary = Boolean(assetCanBeInsertedIntoScript && hideInsertIntoScript);
    const typeBadge = isMultiSelectPreview ? null : getTypeBadge();
    const multiPreviewItems = isMultiSelectPreview ? multiSelectItems.slice(0, 4) : [];
    const multiOverflowCount = Math.max(0, Number(multiSelectCount) - multiPreviewItems.length);
    const isTwoItemContactSheet = Number(multiSelectCount) === 2;

    return (
        <>
            {expanded && (
                <Box
                    onClick={() => setIsExpanded(false)}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(15, 23, 42, 0.08)",
                        zIndex: (theme) => theme.zIndex.modal,
                    }}
                />
            )}
            <Box
                ref={containerRef}
                sx={{
                    position: "fixed",
                    top: expanded ? "50%" : position.top,
                    left: expanded ? "50%" : position.left,
                    transform: expanded ? "translate(-50%, -50%)" : undefined,
                    width: expanded ? "min(920px, calc(100vw - 64px))" : COLLAPSED_WIDTH,
                    height: expanded ? "min(760px, calc(100vh - 96px))" : undefined,
                    borderRadius: expanded ? "16px" : "14px",
                    boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.12)",
                    bgcolor: "#fff",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '14px 16px',
                gap: 1.5,
            }}>
                {/* Type badge */}
                {typeBadge && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            backgroundColor: `${typeBadge.color}10`,
                            color: typeBadge.color,
                            flexShrink: 0,
                        }}
                    >
                        {typeBadge.icon}
                        <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.02em' }}>
                            {typeBadge.label}
                        </Typography>
                    </Box>
                )}
                
                {/* Filename */}
                <Typography 
                    sx={{ 
                        fontSize: 13, 
                        fontWeight: 500, 
                        color: '#111827',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }} 
                    title={isMultiSelectPreview ? multiSelectItems.map((it) => it.name).join(", ") : item?.name}
                >
                    {isMultiSelectPreview
                        ? `${multiSelectCount} selected`
                        : truncateFilename(item?.name || '')}
                </Typography>
                
                {/* Duration (for audio/video) */}
                {duration > 0 && (
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: '#9CA3AF',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                    }}>
                        {formatDuration(duration)}
                    </Typography>
                )}

                {canExpand && (
                    <IconButton
                        size="small"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        title={expanded ? "Shrink preview" : "Expand preview"}
                        sx={{
                            ml: 0.5,
                            color: "#9CA3AF",
                            "&:hover": { backgroundColor: "#F3F4F6", color: "#374151" },
                        }}
                    >
                        {expanded ? <ZoomInMapIcon sx={{ fontSize: 18 }} /> : <ZoomOutMapIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                )}
                
                {/* Close button */}
                <IconButton 
                    size="small" 
                    onClick={handleClose}
                    sx={{ 
                        ml: 0.5,
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            {/* Preview area */}
            <Box
                sx={{
                    px: 2,
                    ...(expanded
                        ? {
                              flex: 1,
                              minHeight: 0,
                              display: "flex",
                              alignItems: "center",
                          }
                        : {}),
                }}
            >
                {/* Image preview */}
                {isMultiSelectPreview && (
                    <Box
                        sx={{
                            width: "100%",
                            height: expanded ? "100%" : 220,
                            borderRadius: "10px",
                            background: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            p: 1,
                            display: "grid",
                            gridTemplateColumns: isTwoItemContactSheet ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
                            gridTemplateRows: isTwoItemContactSheet ? "1fr" : "repeat(2, minmax(0, 1fr))",
                            gap: 1,
                        }}
                    >
                        {multiPreviewItems.length > 0 ? (
                            multiPreviewItems.map((selectedItem, index) => {
                                const hasThumb = Boolean(selectedItem.thumbnailUrl);
                                const showOverflowBadge = multiOverflowCount > 0 && index === multiPreviewItems.length - 1;
                                return (
                                    <Box
                                        key={selectedItem.id}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            borderRadius: "8px",
                                            background: hasThumb
                                                ? "#111827"
                                                : selectedItem.type === "video"
                                                    ? "linear-gradient(135deg, #1F2937 0%, #111827 100%)"
                                                    : "linear-gradient(135deg, #0F766E 0%, #115E59 100%)",
                                            minHeight: 0,
                                        }}
                                    >
                                        {hasThumb ? (
                                            <img
                                                src={selectedItem.thumbnailUrl}
                                                alt={selectedItem.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {selectedItem.type === "video" ? (
                                                    <VideocamOutlinedIcon sx={{ fontSize: 24, color: "rgba(255,255,255,0.9)" }} />
                                                ) : (
                                                    <AudiotrackOutlinedIcon sx={{ fontSize: 24, color: "rgba(255,255,255,0.9)" }} />
                                                )}
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                px: 0.75,
                                                py: 0.5,
                                                background: "linear-gradient(180deg, rgba(17,24,39,0) 0%, rgba(17,24,39,0.85) 100%)",
                                            }}
                                        >
                                            <Typography
                                                title={selectedItem.name}
                                                sx={{
                                                    fontSize: 11,
                                                    lineHeight: 1.2,
                                                    fontWeight: 500,
                                                    color: "#FFFFFF",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {truncateFilename(selectedItem.name, 24)}
                                            </Typography>
                                        </Box>
                                        {showOverflowBadge && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    backgroundColor: "rgba(17, 24, 39, 0.62)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: "#fff",
                                                        fontSize: 26,
                                                        lineHeight: 1,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    +{multiOverflowCount}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })
                        ) : (
                            <Box
                                sx={{
                                    gridColumn: "1 / -1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#6B7280",
                                }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                    {multiSelectCount} selected
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {item?.type === 'image' && previewUrl && (
                    <Box sx={{ 
                        width: '100%', 
                        height: expanded ? "100%" : 200, 
                        borderRadius: '10px', 
                        overflow: 'hidden', 
                        background: '#111',
                    }}>
                        <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                )}
                
                {/* Video preview */}
                {item?.type === 'video' && previewUrl && (
                    <Box sx={{ 
                        width: '100%', 
                        height: expanded ? "100%" : 200, 
                        borderRadius: '10px', 
                        overflow: 'hidden', 
                        background: '#111', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                    }}>
                        <video
                            ref={videoRef}
                            src={previewUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            onLoadedMetadata={() => {
                                if (videoRef.current) {
                                    setDuration(videoRef.current.duration || 0);
                                    setCurrentTime(videoRef.current.currentTime || 0);
                                }
                            }}
                            onTimeUpdate={() => {
                                if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
                            }}
                            onEnded={() => setIsPlaying(false)}
                        />
                    </Box>
                )}
                
                {/* Audio preview - with visual representation */}
                {item?.type === 'audio' && previewUrl && (
                    <Box sx={{ 
                        width: '100%', 
                        height: 100, 
                        borderRadius: '10px', 
                        overflow: 'hidden', 
                        background: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        position: 'relative',
                    }}>
                        {/* Audio waveform visualization placeholder */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            opacity: 0.4,
                        }}>
                            {/* Simple waveform bars */}
                            {[0.3, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 0.5, 0.9, 0.3, 0.6, 0.8, 0.4, 0.7, 0.5].map((h, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 3,
                                        height: `${h * 50}px`,
                                        backgroundColor: '#fff',
                                        borderRadius: '2px',
                                    }}
                                />
                            ))}
                        </Box>
                        
                        {/* Centered audio icon */}
                        <Box sx={{ 
                            position: 'absolute',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                        }}>
                            <AudiotrackOutlinedIcon sx={{ fontSize: 28, color: '#fff', opacity: 0.9 }} />
                        </Box>
                        
                        <audio
                            ref={audioRef}
                            src={previewUrl}
                            onLoadedMetadata={() => {
                                if (audioRef.current) {
                                    setDuration(audioRef.current.duration || 0);
                                    setCurrentTime(audioRef.current.currentTime || 0);
                                }
                            }}
                            onTimeUpdate={() => {
                                if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
                            }}
                            onEnded={() => setIsPlaying(false)}
                        />
                    </Box>
                )}
            </Box>
            
            {/* Playback controls - minimal */}
            {!isMultiSelectPreview && (item?.type === 'audio' || item?.type === 'video') && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    px: 2, 
                    py: 1.5,
                }}>
                    <IconButton 
                        size="small" 
                        onClick={togglePlay}
                        sx={{
                            color: '#374151',
                            '&:hover': { backgroundColor: '#F3F4F6' },
                        }}
                    >
                        {isPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: '#6B7280',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 36,
                    }}>
                        {formatTime(currentTime)}
                    </Typography>
                    <Slider
                        size="small"
                        sx={{ 
                            flex: 1,
                            color: '#9CA3AF',
                            height: 4,
                            '& .MuiSlider-thumb': {
                                width: 12,
                                height: 12,
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: '0 0 0 6px rgba(107, 114, 128, 0.1)',
                                },
                            },
                            '& .MuiSlider-track': {
                                backgroundColor: '#374151',
                            },
                            '& .MuiSlider-rail': {
                                backgroundColor: '#E5E7EB',
                            },
                        }}
                        value={duration ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0}
                        onChange={handleSeek as any}
                    />
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: '#9CA3AF',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 36,
                    }}>
                        {formatTime(duration)}
                    </Typography>
                </Box>
            )}
            
            {/* Actions with helper text */}
            <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                {isMultiSelectPreview ? (
                    <Box sx={{ display: "flex", gap: 0 }}>
                        <Button
                            data-tour="asset-popover-create-sequence"
                            variant="contained"
                            fullWidth
                            onClick={() => { void onCreateSequence?.(); handleClose(); }}
                            sx={{
                                backgroundColor: '#111827',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 500,
                                padding: '10px 16px',
                                borderRadius: '10px',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#1F2937',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            Create Sequence
                        </Button>
                    </Box>
                ) : (
                <Box sx={{ display: 'flex', gap: showInsertIntoScript ? 1.5 : 0 }}>
                    {/* Primary: Insert into script */}
                    {showInsertIntoScript && (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => { if (item) { void onInsert(item); handleClose(); } }}
                                sx={{
                                    backgroundColor: '#111827',
                                    color: '#fff',
                                    textTransform: 'none',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    padding: '10px 16px',
                                    borderRadius: '10px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1F2937',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                Insert into script
                            </Button>
                        </Box>
                    )}
                    
                    {/* Secondary: Add new layer */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Button
                            variant={addLayerIsPrimary ? "contained" : "outlined"}
                            fullWidth
                            onClick={() => { if (item) { void onAddNewLayer(item); handleClose(); } }}
                            sx={{
                                ...(addLayerIsPrimary
                                    ? {
                                          backgroundColor: '#111827',
                                          color: '#fff',
                                      }
                                    : {
                                          color: '#374151',
                                          borderColor: '#E5E7EB',
                                      }),
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 500,
                                padding: '10px 16px',
                                borderRadius: '10px',
                                '&:hover': {
                                    ...(addLayerIsPrimary
                                        ? {
                                              backgroundColor: '#1F2937',
                                          }
                                        : {
                                              backgroundColor: '#F9FAFB',
                                              borderColor: '#D1D5DB',
                                          }),
                                },
                            }}
                        >
                            Add as new layer
                        </Button>
                    </Box>
                </Box>
                )}
            </Box>
            </Box>
        </>
    );
};

export default AssetPreviewPopover;
